import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Typography,
  Box,
  Avatar,
  ListItemAvatar,
  ListItemText
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { user as userApi } from '../../../utils/api';
import { toast } from 'react-toastify';

const UserSelectionDialog = ({ open, onClose, onSelectUser, currentUserId, privateChannels = [] }) => {
  const { t } = useTranslation(['common']);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('');

  useEffect(() => {
    if (open) {
      fetchUsers();
    }
  }, [open, privateChannels]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await userApi.getChatUsers();
      
      // Filter out the current user
      let filteredUsers = response.users.filter(u => u._id !== currentUserId);
      
      // Filter out users that already have DM channels with the current user
      if (privateChannels && privateChannels.length > 0) {
        // Get user IDs from existing private channels
        const existingDmUserIds = privateChannels.reduce((acc, channel) => {
          // Find the other user in each DM channel
          const otherUser = channel.members?.find(member => 
            member._id !== currentUserId
          );
          
          if (otherUser) {
            acc.push(otherUser._id);
          }
          return acc;
        }, []);
        
        console.log('Existing DM user IDs:', existingDmUserIds);
        
        // Filter out users who already have a DM channel
        filteredUsers = filteredUsers.filter(user => 
          !existingDmUserIds.includes(user._id)
        );
        
        console.log('Filtered users after removing existing DM users:', filteredUsers.length);
      }
      
      setUsers(filteredUsers);
      
      // Clear selected user when reopening
      setSelectedUserId('');
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error(t('error.fetchingData'));
    } finally {
      setLoading(false);
    }
  };

  const handleUserSelect = (event) => {
    setSelectedUserId(event.target.value);
  };

  const handleStartChat = () => {
    if (!selectedUserId) {
      toast.warning(t('comms.selectUserFirst'));
      return;
    }
    
    const selectedUser = users.find(user => user._id === selectedUserId);
    if (selectedUser) {
      onSelectUser(selectedUser);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        {t('comms.selectUser')}
      </DialogTitle>
      
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : users.length === 0 ? (
          <Typography color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
            {t('comms.noUsersFound')}
          </Typography>
        ) : (
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel id="user-select-label">{t('comms.selectUserToMessage')}</InputLabel>
            <Select
              labelId="user-select-label"
              value={selectedUserId}
              onChange={handleUserSelect}
              label={t('comms.selectUserToMessage')}
              renderValue={(selected) => {
                const user = users.find(u => u._id === selected);
                if (!user) return "";
                
                return (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Avatar 
                      src={user.imageUrl} 
                      alt={user.name || user.username}
                      sx={{ width: 24, height: 24, mr: 1 }}
                    >
                      {(user.name || user.username)?.[0]}
                    </Avatar>
                    {user.name || user.username}
                  </Box>
                );
              }}
            >
              {users.map((user) => (
                <MenuItem key={user._id} value={user._id}>
                  <ListItemAvatar>
                    <Avatar 
                      src={user.imageUrl} 
                      alt={user.name || user.username}
                      sx={{ width: 32, height: 32 }}
                    >
                      {(user.name || user.username)?.[0]}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText 
                    primary={user.name || user.username}
                    secondary={user.name ? user.username : ''}
                  />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>
          {t('common.cancel')}
        </Button>
        <Button 
          onClick={handleStartChat} 
          color="primary" 
          variant="contained"
          disabled={!selectedUserId || loading}
        >
          {t('comms.startConversation')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserSelectionDialog; 